import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { usePortalAuth } from "./portalAuth";
import { LayoutDashboard, LifeBuoy, ClipboardList, Layers, FileText, LogOut, ShieldCheck } from "lucide-react";
import { XentraIcon } from "@/components/XentraLogo";

const NAV = [
  { to: "/portal/dashboard", label: "Overview", icon: LayoutDashboard, testid: "portal-nav-overview" },
  { to: "/portal/tickets", label: "Support tickets", icon: LifeBuoy, testid: "portal-nav-tickets" },
  { to: "/portal/projects", label: "Projects", icon: ClipboardList, testid: "portal-nav-projects" },
  { to: "/portal/products", label: "Products", icon: Layers, testid: "portal-nav-products" },
  { to: "/portal/cyber-posture", label: "Cyber Posture", icon: ShieldCheck, testid: "portal-nav-cyber" },
  { to: "/portal/invoices", label: "Invoices", icon: FileText, testid: "portal-nav-invoices" },
];

export default function PortalLayout() {
  const { user, loading, logout } = usePortalAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!loading && !user) navigate("/portal/login", { replace: true });
  }, [user, loading, navigate]);

  if (loading || !user) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-slate-500">
          <div className="h-2 w-2 rounded-full bg-blue-600 pulse-dot" />
          <span className="font-mono text-sm">Loading portal…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="portal-layout">
      <aside className="w-64 bg-slate-950 text-slate-200 flex-shrink-0 flex flex-col">
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-white flex items-center justify-center">
              <XentraIcon className="h-7 w-7" />
            </div>
            <div>
              <div className="font-heading text-base font-bold tracking-tight text-white">XENTRA</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-blue-300">Customer Portal</div>
            </div>
          </div>
          <div className="mt-4 text-[10px] uppercase tracking-wider text-slate-500 font-mono">Account</div>
          <div className="text-sm font-medium text-white truncate" data-testid="portal-account-name">{user.account.name}</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-blue-600 text-white"
                    : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-2">
          <div className="px-3 py-2 text-xs text-slate-400">
            <div className="font-medium text-slate-200 truncate">{user.name}</div>
            <div className="font-mono text-[10px] truncate">{user.email}</div>
          </div>
          <button
            onClick={logout}
            data-testid="portal-logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/5 rounded-md"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <div className="max-w-6xl mx-auto p-6 lg:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
